// SAD-458 SRC-07: TanStack Query bindings for refresh dedup exceptions and
// origin adoptions. The RPCs live on zou's ProjectDataService
// (ListObservationExceptions, ListOriginAdoptions, AcceptNewerOrigin); the
// acting user is the authenticated principal server-side, never sent from
// here.
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
const EXCEPTIONS_KEY = 'observation-exceptions';
const ADOPTIONS_KEY = 'origin-adoptions';
export function isPermissionDenied(err) {
    return err instanceof ConnectError && err.code === Code.PermissionDenied;
}
/** Deduped exceptions for one entity, resolved ones included so history stays visible. */ export function useObservationExceptions(nodeId) {
    return useQuery({
        queryKey: [
            EXCEPTIONS_KEY,
            nodeId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listObservationExceptions({
                nodeId,
                includeResolved: true
            });
            return resp.exceptions;
        },
        // A denied viewer stays denied; do not hammer the gateway.
        retry: (count, err)=>!isPermissionDenied(err) && count < 2
    });
}
/** Adopted copies for one entity, superseded ones excluded. */ export function useOriginAdoptions(nodeId, consumerKind = '', consumerId = '') {
    return useQuery({
        queryKey: [
            ADOPTIONS_KEY,
            nodeId,
            consumerKind,
            consumerId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listOriginAdoptions({
                nodeId,
                consumerKind,
                consumerId,
                includeSuperseded: false
            });
            return resp.adoptions;
        },
        retry: (count, err)=>!isPermissionDenied(err) && count < 2
    });
}
function useSourceRefreshMutation(fn, failMsg) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        retry: false,
        onSuccess: ()=>Promise.all([
                queryClient.invalidateQueries({
                    queryKey: [
                        EXCEPTIONS_KEY
                    ]
                }),
                queryClient.invalidateQueries({
                    queryKey: [
                        ADOPTIONS_KEY
                    ]
                }),
                queryClient.invalidateQueries({
                    queryKey: [
                        'observation-conflicts'
                    ]
                })
            ]),
        onError: (err)=>{
            if (isPermissionDenied(err)) {
                toast.error("You don't have access to do this. Request it from your admin or team supervisor.");
                return;
            }
            toast.error(err instanceof Error ? err.message : failMsg);
        }
    });
}
export function useRefreshSources() {
    return useSourceRefreshMutation((args)=>projectDataClient.refreshSourceObservations(args), 'Failed to refresh sources');
}
export function useAcceptNewerOrigin() {
    return useSourceRefreshMutation((args)=>projectDataClient.acceptNewerOrigin(args), 'Failed to accept newer origin');
}
export function useAdoptOriginValue() {
    return useSourceRefreshMutation((args)=>projectDataClient.adoptOriginValue(args), 'Failed to adopt source amount');
}
